import React, { useMemo, useRef } from "react";
import PropTypes from "prop-types";
import FlatPickr from "react-flatpickr";
import "flatpickr/dist/themes/material_blue.css";
import { normalizeTimeValue } from "./schedule-matrix-time-utils";
import {
  SCHEDULE_MATRIX_FLATPICKR_OPTIONS,
  attachFlatpickrScheduleTimeGuards,
  commitFlatpickrScheduleTime,
  lockFlatpickrMainTimeInput,
} from "./schedule-matrix-flatpickr-guards";

const ScheduleMatrixTimeInput = ({
  value,
  onChange,
  className = "",
  invalid = false,
  id,
  placeholder = "00:00:00",
}) => {
  const onChangeRef = useRef(onChange);
  const pickerOpenRef = useRef(false);
  const instanceRef = useRef(null);

  onChangeRef.current = onChange;

  const pickerValue = useMemo(() => {
    const normalized = normalizeTimeValue(value);
    return normalized || "";
  }, [value]);

  const syncToParent = (next) => {
    onChangeRef.current?.(next);
  };

  const commit = (instance) => {
    commitFlatpickrScheduleTime(instance ?? instanceRef.current, syncToParent);
  };

  const flatpickrOptions = useMemo(
    () => ({
      ...SCHEDULE_MATRIX_FLATPICKR_OPTIONS,
      onReady: (_selectedDates, _dateStr, instance) => {
        instanceRef.current = instance;
        attachFlatpickrScheduleTimeGuards(instance, syncToParent);
        lockFlatpickrMainTimeInput(instance.input);
      },
      onOpen: (_selectedDates, _dateStr, instance) => {
        pickerOpenRef.current = true;
        instanceRef.current = instance;
        attachFlatpickrScheduleTimeGuards(instance, syncToParent);
        lockFlatpickrMainTimeInput(instance.input);
      },
      onClose: (_selectedDates, _dateStr, instance) => {
        pickerOpenRef.current = false;
        commit(instance);
      },
      onChange: (_selectedDates, _dateStr, instance) => {
        instanceRef.current = instance;
      },
    }),
    []
  );

  const rootClass = [
    "schedule-matrix-time-input",
    "schedule-matrix-time-picker",
    className,
    invalid ? "is-invalid" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <FlatPickr
      id={id}
      className={rootClass}
      placeholder={placeholder}
      value={pickerValue}
      options={flatpickrOptions}
      onOpen={() => {
        pickerOpenRef.current = true;
      }}
      onClose={() => {
        pickerOpenRef.current = false;
        commit();
      }}
      onBlur={() => {
        window.setTimeout(() => {
          if (!pickerOpenRef.current) {
            commit();
          }
        }, 0);
      }}
      onChange={() => {}}
    />
  );
};

ScheduleMatrixTimeInput.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func,
  className: PropTypes.string,
  invalid: PropTypes.bool,
  id: PropTypes.string,
  placeholder: PropTypes.string,
};

export default ScheduleMatrixTimeInput;
